import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { animate, motion, useMotionValue, useReducedMotion, useScroll, useSpring, useTransform } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Container } from "@/components/shared/PageHeader";
import { TicketTag } from "@/components/shared/EventCard";
import { AmphiArcs } from "@/components/shared/Decor";
import { PhotoCredit } from "@/components/shared/PhotoCredit";
import { PhotoNumberTag } from "@/components/shared/PhotoNumber";
import { imgSrcSet, imgUrl, LQIP_WIDTH } from "@/lib/api";
import { fmtDate, fmtTime } from "@/lib/format";
import { cn } from "@/lib/utils";

/*
 * Front-page hero – the house as a stage.
 *
 * A headline that fills the width, revealed word by word. Beneath it, life in the house sits inside one
 * great proscenium arch (the same arch as the three doors further down), standing half on paper and
 * half on the ink of the programme band. On load a paper curtain – with a thin gold edge of light –
 * rises off the arch; afterwards the house's own photographs cross-fade slowly, breathe with the
 * pointer and drift gently on scroll. The next event hangs on the arch as a ticket. Everything moves
 * with transform/opacity only and stands still under prefers-reduced-motion.
 */

const ease = [0.2, 0.8, 0.2, 1];
const ARCH = { borderRadius: "50% 50% 0 0 / 42% 42% 0 0" };
const SLIDE_MS = 6500;
/* The picture is drawn 12 % larger than the arch so the scroll drift (−3 % … +5 %) and the pointer spring never reach its edge. */
const OVERSCAN = 1.12;

/** Headline split into words, each rising out of its own mask. One word carries the accent. */
const RevealTitle = ({ text, accent = "folket", reduce }) => {
  const clean = (text || "").replace(/\*/g, "");
  const marked = (text || "").match(/\*([^*]+)\*/)?.[1]?.toLowerCase() || accent.toLowerCase();
  const words = clean.split(/\s+/).filter(Boolean);
  return (
    <>
      {words.map((w, i) => {
        const isAccent = w.replace(/[^\p{L}\p{N}]/gu, "").toLowerCase() === marked;
        return (
          <span key={`${w}-${i}`} className="inline-block overflow-hidden pb-[0.08em] pr-[0.22em] align-bottom [margin-bottom:-0.08em]">
            <motion.span
              className={cn("inline-block will-change-transform", isAccent && "text-sol")}
              initial={reduce ? false : { y: "108%" }}
              animate={{ y: 0 }}
              transition={{ duration: 0.9, ease, delay: 0.08 + i * 0.07 }}
            >
              {w}
            </motion.span>
          </span>
        );
      })}
    </>
  );
};

/** A number that counts up once, from zero to its value. Keeps any suffix (e.g. " %"). */
const CountUp = ({ value, reduce, delay = 0.6 }) => {
  const m = String(value).match(/^(\d+)(.*)$/);
  const target = m ? parseInt(m[1], 10) : null;
  const suffix = m ? m[2] : "";
  const [n, setN] = useState(reduce || target === null ? target : 0);
  useEffect(() => {
    if (reduce || target === null) return undefined;
    const c = animate(0, target, { duration: 1.4, ease, delay, onUpdate: (v) => setN(Math.round(v)) });
    return () => c.stop();
  }, [target, reduce, delay]);
  if (target === null) return <>{value}</>;
  return <>{n}{suffix}</>;
};

/** Hero photographs: the figure is ~1360px wide on desktop (×1.12 overscan); on phones the 16:9 picture is cropped to 4:5, so it is wider than the viewport. */
export const HERO_SIZES = "(min-width: 1440px) 1500px, (min-width: 640px) 100vw, 180vw";
export const HERO_WIDTHS = [800, 1200, 1600, 2000];

/**
 * One photograph in the carousel. A 32px blurred thumbnail (a few hundred bytes) paints at once; the sharp
 * picture is only shown once it has *fully* arrived, so a half-downloaded image never leaves a bare strip
 * at the bottom of the arch.
 */
const Slide = ({ src, alt, active, first, onReady, onFail }) => {
  const [ready, setReady] = useState(false);
  const ref = useRef(null);
  const done = () => { setReady(true); onReady?.(); };
  useEffect(() => {
    const el = ref.current;
    if (el?.complete && el.naturalWidth > 0) done();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return (
    <motion.div
      className="absolute inset-0"
      initial={false}
      animate={{ opacity: active ? 1 : 0, scale: active ? 1 : 1.05 }}
      transition={{ opacity: { duration: 1.5, ease }, scale: { duration: 8, ease: "linear" } }}
    >
      <img
        src={imgUrl(src, LQIP_WIDTH)}
        alt=""
        aria-hidden="true"
        loading="eager"
        decoding="async"
        fetchPriority={first ? "high" : "low"}
        className="absolute inset-0 h-full w-full scale-110 object-cover blur-2xl"
      />
      <img
        ref={ref}
        src={imgUrl(src, 1600)}
        srcSet={imgSrcSet(src, HERO_WIDTHS)}
        sizes={HERO_SIZES}
        alt={first ? alt : ""}
        loading="eager"
        decoding={first ? "sync" : "async"}
        fetchPriority={first ? "high" : "low"}
        onLoad={done}
        onError={() => onFail?.()}
        className={cn("absolute inset-0 h-full w-full object-cover transition-opacity duration-500 ease-out", ready ? "opacity-100" : "opacity-0")}
        data-testid={first ? "hero-image" : undefined}
        data-ready={ready ? "true" : "false"}
      />
    </motion.div>
  );
};

/** Slow cross-fade between the house's photographs, inside whatever shape clips it. A picture that fails to load is reported and dropped. */
const Slides = ({ images, alt, index: i = 0, onReady, onFail }) =>
  images.length ? (
    <>
      {images.map((src, n) => (
        <Slide key={src} src={src} alt={alt} active={n === i} first={n === 0} onReady={() => onReady?.(src)} onFail={() => onFail?.(src)} />
      ))}
    </>
  ) : (
    <Skeleton className="absolute inset-0 rounded-none" />
  );

/**
 * The next event, hung on the arch as a paper ticket: a stub with the date, a dashed tear line with notches,
 * and the particulars. No photograph inside – a card on the picture, not a picture in the picture.
 */
export const HeroEventTicket = ({ event, className }) => {
  const multi = event.end_date && event.end_date !== event.date;
  return (
    <Link
      to={`/arrangement/${event.id}`}
      className={cn("group relative block drop-shadow-[0_28px_36px_hsl(var(--ink)/0.5)] transition-transform duration-500 ease-house focus-visible:outline-none lg:hover:-translate-y-1.5", className)}
      data-testid="home-next-event-tile"
      aria-label={`Neste arrangement: ${event.title}`}
    >
      <div className="ticket grid grid-cols-[var(--stub)_1fr] overflow-hidden rounded-[14px] bg-paper text-ink [--stub:92px] group-focus-visible:ring-2 group-focus-visible:ring-sol sm:[--stub:108px]">
        {/* the stub – date */}
        <div className="flex flex-col border-r border-dashed border-ink/30 p-4 sm:p-5" data-testid="home-next-event-date">
          <span className="font-display text-[44px] font-semibold leading-[0.85] tracking-[-0.05em] tabular sm:text-[54px]">{fmtDate(event.date, "d")}</span>
          <span className="t-mono mt-2 text-ink/70">{multi ? `–${fmtDate(event.end_date, "d")} ` : ""}{fmtDate(event.date, "MMM").replace(".", "")}</span>
          <span className="mt-auto pt-4 text-[13px] capitalize text-ink/60">{fmtDate(event.date, "EEEE")}</span>
        </div>
        {/* the particulars */}
        <div className="flex min-w-0 flex-col p-4 sm:p-5">
          <div className="flex items-start justify-between gap-3">
            <span className="t-mono inline-flex min-w-0 items-center gap-x-2.5 whitespace-nowrap text-ink/65">
              <span className="live-dot shrink-0" aria-hidden="true" /><span className="truncate">Neste arrangement</span>
            </span>
            <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-ink/[0.07] text-ink transition-[background-color,color,transform] duration-300 group-hover:scale-105 group-hover:bg-sol" aria-hidden="true"><ArrowUpRight className="h-4 w-4" /></span>
          </div>
          <h2 className="mt-2.5 font-display text-[clamp(19px,1.3vw,24px)] font-semibold leading-[1.08] tracking-[-0.02em] text-balance line-clamp-3">{event.title}</h2>
          <p className="mt-2.5 flex flex-wrap items-center gap-x-3 gap-y-1 text-[13.5px] text-ink/70">
            {event.category && <span>{event.category}</span>}
            {event.start_time && <span>{fmtTime(event.start_time, event.end_time)}</span>}
            {event.location && <span>{event.location}</span>}
            {event.price_text && <span className="text-ink/90">{event.price_text}</span>}
            {event.tickets && <TicketTag t={event.tickets} />}
          </p>
        </div>
      </div>
    </Link>
  );
};

export const HomeHero = ({ title, body, images = [], facts = [], nextEvent, eventsLoading }) => {
  const reduce = useReducedMotion();

  // Scroll: the picture drifts a little, the words a little less – two layers of depth.
  const { scrollY } = useScroll();
  const driftY = useTransform(scrollY, [0, 1200], ["-3%", "5%"]);
  const wordsY = useTransform(scrollY, [0, 700], [0, -36]);

  // Pointer: the picture breathes towards the cursor (desktop only, springs, transform only).
  const figRef = useRef(null);
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const sx = useSpring(mx, { stiffness: 60, damping: 20, mass: 0.6 });
  const sy = useSpring(my, { stiffness: 60, damping: 20, mass: 0.6 });
  const onMove = (e) => {
    if (reduce || !figRef.current || e.pointerType === "touch") return;
    const r = figRef.current.getBoundingClientRect();
    mx.set(((e.clientX - r.left) / r.width - 0.5) * -22);
    my.set(((e.clientY - r.top) / r.height - 0.5) * -14);
  };
  const onLeave = () => { mx.set(0); my.set(0); };

  // Slides – the carousel only moves on to a picture that has fully arrived (see Slide). A picture that cannot be
  // loaded (e.g. not yet present in this database) is dropped from the rotation and the ticks instead of showing
  // an empty frame.
  const [slide, setSlide] = useState(0);
  const [cycle, setCycle] = useState(0);
  const [failed, setFailed] = useState(() => new Set());
  const shown = useMemo(() => images.filter((src) => !failed.has(src)), [images, failed]);
  const ready = useRef(new Set());
  const onReady = (src) => ready.current.add(src);
  const onFail = (src) => setFailed((f) => (f.has(src) ? f : new Set([...f, src])));
  useEffect(() => { if (slide >= shown.length) setSlide(0); }, [shown.length, slide]);
  useEffect(() => {
    if (reduce || shown.length < 2) return undefined;
    const t = setInterval(() => {
      setSlide((n) => { const next = (n + 1) % shown.length; return ready.current.has(shown[next]) ? next : n; });
      setCycle((c) => c + 1);
    }, SLIDE_MS);
    return () => clearInterval(t);
  }, [shown, reduce, cycle]);
  const go = (n) => { setSlide(n); setCycle((c) => c + 1); };

  return (
    <section className="overflow-x-clip" data-testid="home-hero">
      <Container className="relative pt-8 sm:pt-10 lg:pt-12">
        <AmphiArcs className="pointer-events-none absolute -top-16 right-[-2%] hidden w-[640px] text-foreground/[0.09] [mask-image:linear-gradient(to_bottom,black_30%,transparent_85%)] lg:block" animate={!reduce} dot={false} />

        <motion.div style={reduce ? undefined : { y: wordsY }} className="relative will-change-transform">
          <h1
            className="font-display text-[clamp(46px,7.7vw,140px)] font-semibold leading-[0.92] tracking-[-0.04em] text-balance lg:max-w-[13ch]"
            data-testid="hero-title"
          >
            <RevealTitle text={title} accent="folket" reduce={reduce} />
          </h1>

          <div className="mt-7 grid gap-7 sm:mt-8 lg:mt-9 lg:grid-cols-12 lg:items-end lg:gap-10">
            <motion.div
              className="flex flex-col gap-6 lg:col-span-6"
              initial={reduce ? false : { opacity: 0, y: 14 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, ease, delay: 0.45 }}
            >
              <p className="t-lead max-w-xl text-muted-foreground text-pretty" data-testid="hero-text">{body}</p>
              <div className="flex flex-wrap gap-3">
                <Button asChild size="lg" data-testid="hero-booking-button"><Link to="/leig-rom">Leig rom <ArrowUpRight className="h-4 w-4" /></Link></Button>
                <Button asChild variant="outline" size="lg" data-testid="hero-arranger-button"><Link to="/arranger">Arranger noko</Link></Button>
              </div>
            </motion.div>
            <motion.ul
              className="grid grid-cols-2 gap-x-6 gap-y-4 border-t border-border pt-5 sm:flex sm:flex-wrap sm:gap-x-9 lg:col-span-5 lg:col-start-8 lg:justify-end lg:border-0 lg:pt-0"
              initial={reduce ? false : { opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, ease, delay: 0.55 }}
              data-testid="hero-facts"
            >
              {facts.map((f, i) => (
                <li key={f.label} className="flex flex-col gap-1">
                  <span className="font-display text-[28px] font-semibold leading-none tracking-[-0.03em] tabular sm:text-[32px]"><CountUp value={f.value} reduce={reduce} delay={0.6 + i * 0.08} /></span>
                  <span className="t-mono text-muted-foreground">{f.label}</span>
                </li>
              ))}
            </motion.ul>
          </div>
        </motion.div>
      </Container>

      {/* The proscenium: one arch, standing half on paper, half on the ink of the programme band. */}
      <div className="relative mt-10 sm:mt-12 lg:mt-12">
        <div className="grain absolute inset-x-0 bottom-0 top-[46%] bg-ink sm:top-[52%]" aria-hidden="true">
          {/* stage light spilling onto the floor */}
          <div className="absolute inset-x-0 bottom-0 top-0 bg-[radial-gradient(60%_70%_at_50%_100%,hsl(var(--sol)/0.14),transparent_70%)]" />
        </div>
        <Container className="relative">
          <div className="relative" data-testid="hero-images">
            <motion.figure
              ref={figRef}
              onPointerMove={onMove}
              onPointerLeave={onLeave}
              className="relative isolate overflow-hidden bg-ink aspect-[4/5] [transform:translateZ(0)] sm:aspect-[16/10] lg:aspect-[21/9]"
              style={ARCH}
              initial={reduce ? false : { opacity: 0, y: 26 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 1, ease, delay: 0.3 }}
              data-testid="hero-photo"
            >
              <motion.div className="absolute inset-0 will-change-transform" style={reduce ? undefined : { y: driftY }}>
                {/* Overscan lives in the same transform as the pointer spring – a Tailwind `scale-*` class would be
                    overridden by Framer Motion's inline transform, leaving the drift to expose a bare strip at the bottom. */}
                <motion.div className="absolute inset-0 will-change-transform" style={reduce ? undefined : { x: sx, y: sy, scale: OVERSCAN }}>
                  <Slides images={shown} alt="Fullt hus i King Oscar Amfi" index={slide} onReady={onReady} onFail={onFail} />
                </motion.div>
              </motion.div>

              {/* a quiet inner hairline – the second rule of the frame */}
              <span aria-hidden="true" className="pointer-events-none absolute inset-x-2.5 top-2.5 bottom-0 z-10 border border-b-0 border-paper/25" style={ARCH} />

              {/* the curtain – paper that lifts off the stage once, led by a thin edge of light */}
              {!reduce && (
                <motion.div
                  className="pointer-events-none absolute inset-0 z-20 bg-background will-change-transform after:absolute after:inset-x-0 after:bottom-0 after:h-[3px] after:bg-sol after:content-['']"
                  initial={{ y: 0 }}
                  animate={{ y: "-102%" }}
                  transition={{ duration: 1.35, ease, delay: 0.6 }}
                  aria-hidden="true"
                />
              )}

              {/* the one point of light at the top of the arch */}
              <span className="pointer-events-none absolute left-1/2 top-[7%] z-10 h-2 w-2 -translate-x-1/2 rounded-full bg-sol shadow-[0_0_24px_6px_hsl(var(--sol)/0.35)] sm:top-[6%]" aria-hidden="true" />
            </motion.figure>

            {/* rhythm of the slides: only the ticks – the pictures speak for themselves (plus the photographer's credit) */}
            <div className="mt-4 flex items-center justify-between gap-6 text-paper/60 sm:mt-5" data-testid="hero-caption">
              <span className="flex min-w-0 items-center gap-3">
                <span className="min-w-0 truncate">{shown[slide] && <PhotoCredit src={shown[slide]} className="!text-paper/50" testId="hero-credit" />}</span>
                {shown[slide] && <PhotoNumberTag src={shown[slide]} interactive tone="paper" className="shrink-0 !bg-paper/15 !text-paper/80 hover:!bg-paper/30" testId="hero-photo-number" />}
              </span>
              {shown.length > 1 && (
                <ul className="flex items-center gap-1.5" aria-label="Bilete i heroen">
                  {shown.map((src, n) => (
                    <li key={src}>
                      <button
                        type="button"
                        onClick={() => go(n)}
                        className={cn("relative block h-1 overflow-hidden rounded-full transition-[width,background-color] duration-500 ease-house", n === slide ? "w-10 bg-paper/25" : "w-3 bg-paper/30 hover:bg-paper/60")}
                        aria-label={`Vis bilete ${n + 1}`}
                        aria-current={n === slide ? "true" : undefined}
                        data-testid={`hero-slide-${n}`}
                      >
                        {n === slide && <span key={`${slide}-${cycle}`} className={cn("absolute inset-0 origin-left rounded-full bg-sol", !reduce && "hero-progress")} style={{ "--dur": `${SLIDE_MS}ms` }} aria-hidden="true" />}
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            {/* next event – a paper ticket hung on the arch (desktop), laid beneath it (mobile) */}
            {eventsLoading ? (
              <Skeleton className="mt-4 h-[168px] rounded-[14px] bg-paper/10 lg:absolute lg:bottom-[12%] lg:right-[4%] lg:mt-0 lg:w-[min(440px,34%)]" />
            ) : nextEvent ? (
              <motion.div
                className="relative mt-4 lg:absolute lg:bottom-[12%] lg:right-[4%] lg:mt-0 lg:w-[min(440px,34%)]"
                initial={reduce ? false : { opacity: 0, y: 22 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.9, ease, delay: 1.5 }}
              >
                <HeroEventTicket event={nextEvent} />
              </motion.div>
            ) : null}
          </div>
        </Container>
      </div>
    </section>
  );
};
